import { ref, type Ref } from 'vue'
import { getAgendorPanel, type AgendorPanel, type AgendorPanelResult } from '@/services/wootdashAgendor'
import { setAgendorLink } from '@/composables/useAgendor'

// Estado do painel do Agendor de UMA conversa, compartilhado entre o menu do cabeçalho e o painel lateral. Toda ação
// do servidor devolve o painel já atualizado — aqui só troca o estado (e o ícone do contato, pelo vínculo).

/** Seção em que o painel abre (o item do menu escolhido). */
export type AgendorPanelFocus = 'send' | 'deal' | 'task' | 'note' | 'owner' | null

export interface AgendorPanelState {
  panel: Ref<AgendorPanel | null>
  loading: Ref<boolean>
  busy: Ref<boolean>
  problem: Ref<string | null>
  load: (reload?: boolean) => Promise<void>
  run: (action: () => Promise<AgendorPanelResult>) => Promise<boolean>
  /** Mensagem pra mostrar no painel (validação da tela, falha de uma chamada fora do painel). */
  setProblem: (message: string | null) => void
}

export function useAgendorPanel(token: () => string, contactId: () => string | null | undefined): AgendorPanelState {
  const panel = ref<AgendorPanel | null>(null)
  const loading = ref(false)
  const busy = ref(false)
  const problem = ref<string | null>(null)
  let seq = 0

  function apply(result: AgendorPanelResult) {
    const id = contactId()
    if (result.panel) {
      panel.value = result.panel
      if (id) setAgendorLink(id, result.panel.link)
    }
    problem.value = result.problem ?? result.panel?.problem ?? null
  }

  async function load(reload = false) {
    const id = contactId()
    if (!id) return
    const mine = ++seq
    loading.value = true
    const result = await getAgendorPanel(token(), id, reload)
    if (mine !== seq) return
    loading.value = false
    apply(result)
  }

  async function run(action: () => Promise<AgendorPanelResult>) {
    busy.value = true
    problem.value = null
    try {
      const result = await action()
      apply(result)
      return result.ok
    } finally {
      busy.value = false
    }
  }

  return { panel, loading, busy, problem, load, run, setProblem: (message: string | null) => { problem.value = message } }
}
